// Package imports:
import React from 'react';
// Component imports:
import ColoredNumber from '../../ui-elements/ColoredNumber/ColoredNumber';
import DisplayBox from '../DisplayBox/DisplayBox';
import PriceRange from '../../ui-elements/PriceRange/PriceRange';
import LmdSingleLineTable from '../../ui-elements/Table/LmdSingleLineTable';
// Service imports:
import { formatNumber, convertToPercentage, getAPItoday } from '../../services/utils';
import { useApiLmdData } from '../../services/apiHooks';
// Type imports:
import { IDefaultProps } from '../../types/Types';
import { IApiLmdIndexStatisticList } from '../../types/MarketTypes';
import { IndexTypes } from '../SingleIndexPage/SingleIndexPage';

interface IOwnProps {
    index?: string,
    setIndexName(name: string | undefined): void,
}
type Props = IDefaultProps & IOwnProps & React.HTMLAttributes<HTMLDivElement>;

const SingleIndex: React.FC<Props> = ({
    accessToken,
    refreshRateMs,
    index = 'OMXN40',
    setIndexName,
    ...props
}) => {
    const [indices] = useApiLmdData<IApiLmdIndexStatisticList[]>(
        `/v1/market_data/v1/indexes/symbol/${index}/index_statistics_list?trading_date=${getAPItoday()}`,
        accessToken,
        refreshRateMs
    );

    const nameChanger = (symbol: IndexTypes) => {
        if(symbol === 'OMXN40'){
            return ('OMXN40 - Nordic')
        }
        if(symbol === 'OMXC20CAP'){
            return ('OMXC20CAP - Danmörk')
        }
        if(symbol ===  'OMXS30'){
            return ('OMXS30 - Svíþjóð')
        }
        if(symbol ===  'OMXH25'){
            return ('OMXH25 - Finnland')
        }
        return symbol;
    }

    return (
        <DisplayBox
            title={nameChanger(index as IndexTypes)}
            {...props}
        >
            <LmdSingleLineTable 
                apiData={indices}
                tableSize='lg'
                columns={[{
                    title: 'Gildi',
                    dataAccessor: ( {last_value} ) => (formatNumber(last_value, '-', 2)),
                    textAlign: 'left'
                }, {
                    title: 'Dagsbreyting',
                    dataAccessor: ( {yesterdays_value_change_per} ) => (
                        <ColoredNumber  
                            sign={(yesterdays_value_change_per === null) ? 'neutral' : (yesterdays_value_change_per < 0) ? 'neg' : (yesterdays_value_change_per > 0) ? 'pos' : 'neutral'}
                        >
                                {convertToPercentage(yesterdays_value_change_per, true, 2)}
                        </ColoredNumber>
                    ),
                    textAlign: 'left'
                }, {
                    title: '1 Mán.',
                    dataAccessor: ( {weeks4_value_change_per} ) => (
                        <ColoredNumber  
                            sign={(weeks4_value_change_per === null) ? 'neutral' : (weeks4_value_change_per < 0) ? 'neg' : (weeks4_value_change_per > 0) ? 'pos' : 'neutral'}
                        >
                            {convertToPercentage(weeks4_value_change_per, true, 2)}
                        </ColoredNumber>
                    ),
                    textAlign: 'left'
                }, {
                    title: 'Innan árs',
                    dataAccessor: ( {year_value_change_per  }) => (
                        <ColoredNumber  
                            sign={(year_value_change_per === null) ? 'neutral' : (year_value_change_per < 0) ? 'neg' : (year_value_change_per > 0) ? 'pos' : 'neutral'}
                        >
                            {convertToPercentage(year_value_change_per, true, 2)}
                        </ColoredNumber>
                    ),
                    textAlign: 'left'
                }, {
                    title: '1 Ár',
                    dataAccessor: ( { months12_value_change_per } ) => (
                        <ColoredNumber  
                            sign={(months12_value_change_per === null) ? 'neutral' : (months12_value_change_per < 0) ? 'neg' : (months12_value_change_per > 0) ? 'pos' : 'neutral'}
                        >
                            {convertToPercentage(months12_value_change_per, true, 2)}
                        </ColoredNumber>),
                    textAlign: 'left'
                }, {
                    title: 'Árs bil',
                    dataAccessor: ( { months12_low_value, months12_high_value, last_value } ) => (
                        <PriceRange low={months12_low_value} high={months12_high_value} value={last_value} />
                    ),
                    textAlign: 'left'
                }]}
            />
        </DisplayBox>
    );
}

export default SingleIndex;